import type { Region } from "@gooner/shared";
import { syncLeaderOf } from "@gooner/shared";
import { regionChromeButton } from "../ui/primitives";
import { IconMirror } from "../ui/icons";
import { track } from "../../analytics";

/**
 * The mirror toggle riding a SYNCED region's tile, beside the link button — one press
 * flips the pair's playback across a vertical axis, another puts it back. Shared by
 * the editor's canvas and the wall's hover chrome, like RegionLinkButton; the panel's
 * Same/Mirrored buttons in SyncControl write the same field with more ceremony.
 *
 * Renders NOTHING on an unsynced region: mirroring is a property of a sync (it is
 * `syncMode` on a follower), so on a lone region the button would be a switch wired
 * to nothing. The host applies `toggleMirror`, which flips the clicked follower — or,
 * clicked on a leader, every follower it has: "mirror this pair" from either side.
 *
 * Every pointer event stops here, same reason as the link button: the tile underneath
 * starts a move (editor) or opens the feed popup (wall).
 */
export function RegionMirrorButton({
  region,
  allRegions,
  onToggle,
}: {
  region: Region;
  allRegions: Region[];
  onToggle: () => void;
}) {
  const leader = syncLeaderOf(region, allRegions);
  const followers = allRegions.filter(
    (r) => r.syncWith === region.id && syncLeaderOf(r, allRegions) !== null,
  );
  if (!leader && followers.length === 0) return null; // not synced — nothing to mirror
  const mirrored = leader
    ? (region.syncMode ?? "normal") === "flip"
    : followers.every((f) => (f.syncMode ?? "normal") === "flip");

  return (
    <div
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        aria-pressed={mirrored}
        title={mirrored ? "Un-mirror — play the same way up" : "Mirror this synced pair"}
        onClick={(e) => {
          // Focus dropped so a following Space means the surface's own gesture
          // (skip-all on the wall), not a synthesized re-click of this button.
          e.currentTarget.blur();
          track("region_mirror_set", { on: !mirrored });
          onToggle();
        }}
        className={regionChromeButton(mirrored)}
      >
        <IconMirror width={14} height={14} />
      </button>
    </div>
  );
}
